import { memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { LogoRefresh } from "./LogoRefresh";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { CHULA } from "@chula/shared";
import { ageChipText } from "../lib/tierDisplay";
import type { AcademicSnapshot, FallbackTier } from "@chula/shared";
import { useTheme } from "../hooks/useTheme";
import { useDismiss } from "../hooks/useDismiss";
import { PipelinePill, type AdapterHealth } from "./AdapterHealthPanel";
import { loadPublicSheetsUrl } from "./SheetsPanel";
import {
  hasOperatorToken,
  setOperatorToken,
  subscribeOperatorToken,
} from "../lib/operatorToken";

interface FeedHealth {
  label: string;
  tier: FallbackTier | "loading";
  ageMinutes: number;
}

interface Props {
  feeds: FeedHealth[];
  onOpenCatalog: () => void;
  catalogCount: number;
  viewMode: "2D" | "3D" | "3DS";
  onCycleViewMode: () => void;
  onOpenManual: () => void;
  onOpenSheets: () => void;
  onOpenAnalytics?: () => void;
  onOpenCameras?: () => void;
  onSkipToMap?: () => void;
  /** Jump to MOB lens + shuttle layers (deep-link target for FLEET). */
  onOpenFleet?: () => void;
  /** Live bus count when GPS is wired; otherwise null → show awaiting. */
  fleetLiveCount?: number | null;
  sheetsConfigured: boolean;
  academic: AcademicSnapshot | null;
  adapterHealth: AdapterHealth[];
  /**
   * Campus status slot rendered right after the brand — the integrator passes
   * <StatusBadge /> ("ATTENTION · N" / "ALL CLEAR"). Optional so the bar
   * renders unchanged without it.
   */
  statusBadge?: ReactNode;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();
}

const FEED_GLOSSARY: Record<string, string> = {
  NEWS: "Campus news feed",
  CR:   "Citizen / Telegram campus reports",
  AQ:   "Air quality (PM2.5 / AQI)",
  WX:   "Weather (Open-Meteo)",
  BUS:  "CU POP Bus — simulated on the published timetable until the ViaBus GPS feed is wired (scenario tier)",
  iTIC: "Traffic incidents near campus (iTIC — Bangkok traffic information centre)",
};

/**
 * Plain words on the chips (design review 2026-09): an executive should not
 * have to decode CR / AQ / WX / iTIC. Keys stay the feed ids App.tsx passes.
 */
const FEED_DISPLAY: Record<string, string> = {
  CR: "REPORTS",
  AQ: "AIR",
  WX: "WEATHER",
  iTIC: "TRAFFIC",
};

/** Grey word tag per non-live tier; live shows nothing extra. */
const FEED_TIER_TAG: Partial<Record<FeedHealth["tier"], string>> = {
  database: "DB",
  cache: "CACHED",
  scenario: "SIM",
  reference: "REF",
  unavailable: "OFFLINE",
};

/** Tier words for the status chips — the dot's colour/shape is never the only signal. */
const FEED_TIER_WORD: Record<FeedHealth["tier"], string> = {
  live: "live",
  database: "saved records",
  cache: "stale copy",
  scenario: "simulated",
  reference: "reference",
  unavailable: "offline",
  loading: "connecting",
};

/** Jump past the top bar to the map (WCAG 2.4.1). <main> has no id, so focus it directly. */
function skipToMain(e: React.MouseEvent<HTMLAnchorElement>) {
  const main = document.querySelector<HTMLElement>("main");
  if (!main) return;
  e.preventDefault();
  if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
  if (main.inert) requestAnimationFrame(() => main.focus());
  else main.focus();
}

const TEMPO_COLOR: Record<AcademicSnapshot["tempo"], string> = {
  low: "var(--text-3)",
  normal: "var(--data)",
  high: "var(--warn)",
  peak: "var(--bad)",
};

export const TopBar = memo(function TopBar({
  feeds,
  onOpenCatalog,
  catalogCount,
  viewMode,
  onCycleViewMode,
  onOpenManual,
  onOpenSheets,
  onOpenAnalytics,
  onOpenCameras,
  onSkipToMap,
  onOpenFleet,
  fleetLiveCount = null,
  sheetsConfigured,
  academic,
  adapterHealth,
  statusBadge,
}: Props) {
  const [now, setNow] = useState(() => new Date());
  // Below 1300px the full button row no longer fits the fixed 44px bar; the
  // secondary destinations fold into MORE so every control stays one click
  // away instead of being clipped off the right edge.
  const condensed = useMediaQuery("(max-width: 1300px)");
  const [moreOpen, setMoreOpen] = useState(false);
  const [operatorSignedIn, setOperatorSignedIn] = useState<boolean>(() => hasOperatorToken());
  const [operatorPromptOpen, setOperatorPromptOpen] = useState(false);
  const [operatorInput, setOperatorInput] = useState("");
  const moreRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const operatorRef = useRef<HTMLDivElement>(null);
  const operatorBtnRef = useRef<HTMLButtonElement>(null);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const closeOperator = useCallback(() => {
    setOperatorPromptOpen(false);
    setOperatorInput("");
  }, []);
  useDismiss(moreOpen, closeMore, moreRef, moreBtnRef);
  useDismiss(operatorPromptOpen, closeOperator, operatorRef, operatorBtnRef);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  // Repaint when the operator signs in/out anywhere (currently only here,
  // but kept generic so future surfaces — keyboard shortcut, deep link —
  // stay in sync without prop-drilling).
  useEffect(() => {
    return subscribeOperatorToken(() => setOperatorSignedIn(hasOperatorToken()));
  }, []);

  const { theme, toggle } = useTheme();
  const liveCount = feeds.filter((f) => f.tier === "live").length;
  const publicSheetUrl = useMemo(() => loadPublicSheetsUrl(), [sheetsConfigured]);

  return (
    <header className="topbar">
      <a className="skip-link" href="#main" onClick={(event) => { onSkipToMap?.(); skipToMain(event); }}>
        Skip to map
      </a>
      <div className="brand">
        <LogoRefresh />
        <a
          className="sponsor"
          href="https://slic-index.nonarkara.org"
          target="_blank"
          rel="noreferrer"
          aria-label="SLIC Index — Smart Liveable Cities"
        >
          <img src="/logos/slic.png" alt="SLIC" />
        </a>
        <a
          className="sponsor"
          href="https://www.depa.or.th"
          target="_blank"
          rel="noreferrer"
          aria-label="depa — Digital Economy Promotion Agency"
        >
          <img src="/logos/depa.jpg" alt="depa" />
        </a>
        <span className="brand-sep" aria-hidden />
        <div className="brand-stack">
          <strong>Chula Control Tower</strong>
          <span className="brand-sub mono">
            {CHULA.name.en} · Bangkok
          </span>
        </div>
        {statusBadge}
      </div>

      {/* Status dots only — not toggles. Opens SOURCES for detail. */}
      <button
        type="button"
        className="feeds feeds-as-button"
        onClick={onOpenCatalog}
        title="Open source health catalog"
        aria-label={`Data status: ${liveCount} of ${feeds.length} feeds live (${feeds
          .map((f) => `${FEED_GLOSSARY[f.label] ?? f.label}: ${FEED_TIER_WORD[f.tier]}`)
          .join("; ")}) — open sources`}
      >
        <span className="feeds-label">FEEDS</span>
        {feeds.map((f) => (
          <span
            key={f.label}
            className={`feed-chip feed-${f.tier}`}
            title={`${FEED_GLOSSARY[f.label] ?? f.label} · ${f.tier} · ${f.tier === "loading" ? "Loading" : ageChipText(f.tier, f.ageMinutes)}`}
          >
            <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} aria-hidden />
            {FEED_DISPLAY[f.label] ?? f.label}
            {FEED_TIER_TAG[f.tier] && <span className="tier-tag">{FEED_TIER_TAG[f.tier]}</span>}
          </span>
        ))}
        <span className="live-count mono">
          {liveCount}/{feeds.length} LIVE
        </span>
      </button>

      <div className="topbar-right">
        <PipelinePill adapters={adapterHealth} />
        {publicSheetUrl && (
          <a
            href={publicSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mono sheets-btn sheets-btn-live live-data-btn"
            aria-label="Open the live-data Google Sheet, read-only"
            title="Public live-data sheet"
          >
            DATA
          </a>
        )}
        {!condensed && <button onClick={onOpenCatalog} className="mono" aria-label="Open source catalog">
          SOURCES · {catalogCount}
        </button>}
        {!condensed && onOpenAnalytics && (
          <button
            type="button"
            onClick={onOpenAnalytics}
            className="mono"
            aria-label="Open the Campus Data Observatory"
            title="Campus Data Observatory — the reference figures that need no map: rankings, enrollment, research, finance, campus baseline, PMCU portfolio"
          >
            ANALYTICS
          </button>
        )}
        {!condensed && onOpenFleet && (
          <button
            type="button"
            onClick={onOpenFleet}
            className={`mono fleet-btn${fleetLiveCount != null ? " fleet-btn-live" : ""}`}
            aria-label="Open CU POP Bus fleet view"
            title={
              fleetLiveCount != null
                ? `${fleetLiveCount} live CU POP Bus vehicles`
                : "Simulated fleet on the published timetable — real road loops, 20 km/h, 60 s stops. Live GPS lands here when ViaBus is wired."
            }
          >
            {fleetLiveCount != null ? `FLEET · ${fleetLiveCount}` : "FLEET · SIM"}
          </button>
        )}
        <div className="topbar-more" ref={moreRef}>
          <button
            ref={moreBtnRef}
            type="button"
            className="mono topbar-more-btn"
            aria-expanded={moreOpen}
            aria-haspopup="menu"
            onClick={() => setMoreOpen((v) => !v)}
            title="Sheets, help, links"
          >
            MORE
          </button>
          {moreOpen && (
            <div className="topbar-more-menu" role="menu">
              {onOpenCameras && <button type="button" role="menuitem" className="mono" onClick={() => { setMoreOpen(false); onOpenCameras(); }}>Camera operations</button>}
              {condensed && (
                <>
                  <button type="button" role="menuitem" className="mono" onClick={() => { setMoreOpen(false); onOpenCatalog(); }}>
                    Sources · {catalogCount}
                  </button>
                  {onOpenAnalytics && (
                    <button type="button" role="menuitem" className="mono" onClick={() => { setMoreOpen(false); onOpenAnalytics(); }}>
                      Analytics
                    </button>
                  )}
                  {onOpenFleet && (
                    <button type="button" role="menuitem" className="mono" onClick={() => { setMoreOpen(false); onOpenFleet(); }}>
                      {fleetLiveCount != null ? `Fleet · ${fleetLiveCount} live` : "Fleet · simulated"}
                    </button>
                  )}
                  <hr className="topbar-more-sep" aria-hidden />
                </>
              )}
              <button
                type="button"
                role="menuitem"
                className="mono"
                onClick={() => { setMoreOpen(false); onOpenSheets(); }}
              >
                Sheets setup
              </button>
              <button
                type="button"
                role="menuitem"
                className="mono"
                onClick={() => { setMoreOpen(false); onOpenManual(); }}
              >
                Manual
              </button>
              <a
                role="menuitem"
                href="/install.html"
                className="mono"
                onClick={() => setMoreOpen(false)}
              >
                Android &amp; iPhone web app
              </a>
              <a
                role="menuitem"
                href="/about.html"
                target="_blank"
                rel="noopener noreferrer"
                className="mono"
                onClick={() => setMoreOpen(false)}
              >
                About
              </a>
              <a
                role="menuitem"
                href="https://bkk.nonarkara.org/atlas/ratchathewi"
                target="_blank"
                rel="noopener noreferrer"
                className="mono"
                onClick={() => setMoreOpen(false)}
                title="BKKx 3D MapLibre atlas — Ratchathewi (north of campus)"
              >
                BKKx atlas · Ratchathewi
              </a>
              <a
                role="menuitem"
                href="https://bkk.nonarkara.org/atlas/historic-core"
                target="_blank"
                rel="noopener noreferrer"
                className="mono"
                onClick={() => setMoreOpen(false)}
                title="BKKx 3D MapLibre atlas — Historic Core"
              >
                BKKx atlas · Historic Core
              </a>
              <a
                role="menuitem"
                href="https://arnismc.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mono"
                onClick={() => setMoreOpen(false)}
                title="Arnis — OSM → Minecraft city generator"
              >
                Arnis / Minecraft 3D
              </a>
            </div>
          )}
        </div>
        <button
          onClick={onCycleViewMode}
          className={`mono dim-toggle vm-${viewMode}`}
          aria-label={`View mode: ${viewMode}. Click to cycle 2D → 3D → 3D substructure`}
          title={
            viewMode === "2D"
              ? "Tap → 3D · extrude buildings"
              : viewMode === "3D"
                ? "Tap → 3DS · underground"
                : "Tap → 2D · top-down"
          }
        >
          {viewMode}
        </button>
        <div className="topbar-operator" ref={operatorRef}>
          <button
            ref={operatorBtnRef}
            type="button"
            className={`mono topbar-operator-btn${operatorSignedIn ? " signed-in" : ""}`}
            aria-expanded={operatorPromptOpen}
            aria-haspopup="dialog"
            onClick={() => { setOperatorPromptOpen((v) => !v); setOperatorInput(""); }}
            title={
              operatorSignedIn
                ? "Operator session active — click to sign out"
                : "Sign in as operator (required for Mark Fixed / Archive / Reply)"
            }
          >
            {operatorSignedIn ? "OPERATOR ✓" : "OPERATOR"}
          </button>
          {operatorPromptOpen && (
            <div className="topbar-operator-menu" role="dialog" aria-label="Operator sign-in">
              {operatorSignedIn ? (
                <>
                  <span className="caption mono">Operator session active.</span>
                  <button
                    type="button"
                    className="mono"
                    onClick={() => { setOperatorToken(null); setOperatorPromptOpen(false); }}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <label className="caption mono" htmlFor="operator-token-input">
                    Operator token
                  </label>
                  <input
                    id="operator-token-input"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    className="mono"
                    placeholder="paste operator token"
                    value={operatorInput}
                    onChange={(e) => setOperatorInput(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && operatorInput.trim()) {
                        setOperatorToken(operatorInput);
                        closeOperator();
                        operatorBtnRef.current?.focus();
                      }
                    }}
                  />
                  <span className="caption mono" style={{ color: "var(--ink-mid)" }}>
                    Held in memory only — cleared on reload.
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        <button
          onClick={toggle}
          className="theme-toggle"
          aria-label={`Theme: ${theme}. Click to cycle light → dark → high-contrast`}
          title={theme === "dark" ? "Switch to high-contrast theme" : theme === "light" ? "Switch to dark theme" : "Switch to light theme"}
        >
          {theme === "dark" ? "☀" : theme === "light" ? "◑" : "◆"}
        </button>
      </div>

      <div className="clock">
        {academic?.current && (
          <span
            className="mono academic-chip"
            style={{ color: TEMPO_COLOR[academic.tempo] }}
            title={`${academic.current.label} · ${academic.current.describe}${
              academic.next && academic.daysToNext != null
                ? ` · next: ${academic.next.label} in ${academic.daysToNext}d`
                : ""
            }`}
          >
            {academic.current.label.toUpperCase()}
          </span>
        )}
        <span className="clock-date">{formatDate(now)}</span>
        <span className="clock-time">
          {now.toLocaleTimeString("en-GB", { hour12: false })}
        </span>
      </div>
    </header>
  );
});
